import React, { useEffect, useState } from "react";
import styles from "./NavSlider.module.css";
import { useNavigate, useParams } from "react-router-dom";

export default function NavSlider({
  isOpen = false,
  onClose,
}) {
  const navigate = useNavigate();

  /* =========================================================
     URL PARAMETERS
  ========================================================= */

  const { userId, deviceUuid } = useParams();

  /* =========================================================
     BASE PATH
  ========================================================= */

  const basePath =
    userId && deviceUuid
      ? `/${userId}/${deviceUuid}`
      : "";

  /* =========================================================
     ACTIVE MENU ITEM
  ========================================================= */

  const [activeItem, setActiveItem] = useState("Profile");

  /* =========================================================
     USER DATA
  ========================================================= */

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [userError, setUserError] = useState("");

  /* =========================================================
     IMAGE ERROR STATE (FALLBACK)
  ========================================================= */

  const [imageError, setImageError] = useState(false);

  /* =========================================================
     PHP API
  ========================================================= */

  const API_URL =
    "https://coinyoco.world/brobizee-api/get_user.php";

  /* =========================================================
     FETCH USER DATA
  ========================================================= */

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoadingUser(true);
        setUserError("");
        setImageError(false);

        if (!userId) {
          setUserError("User ID not found.");
          setUser(null);
          return;
        }

        console.log("NavSlider User ID:", userId);

        const response = await fetch(
          `${API_URL}?user_id=${encodeURIComponent(
            userId
          )}`
        );

        const responseText = await response.text();

        console.log(
          "NavSlider RAW USER RESPONSE:",
          responseText
        );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}: ${responseText}`
          );
        }

        let data;

        try {
          data = JSON.parse(responseText);
        } catch (jsonError) {
          console.error(
            "Invalid JSON from PHP:",
            responseText
          );
          throw new Error(
            "PHP API returned invalid JSON."
          );
        }

        console.log("NavSlider USER DATA:", data);

        if (data.success) {
          setUser(data.user);
        } else {
          setUser(null);
          setUserError(
            data.message || "Unable to fetch user data."
          );
        }
      } catch (error) {
        console.error(
          "NavSlider user fetch error:",
          error
        );

        setUser(null);
        setUserError(
          error.message ||
            "Something went wrong while loading user."
        );
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUser();
  }, [userId]);

  /* =========================================================
     NAVIGATION ITEMS
  ========================================================= */

  const navItems = [
    {
      label: "Profile",
      id: "profile",
      path: "/edit_profile",
      icon: (
        <svg
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-4.2 3.6-7 8-7s8 2.8 8 7" />
        </svg>
      ),
    },

    {
      label: "Privacy Policy",
      id: "privacy",
      path: "/privacy-policy",
      icon: (
        <svg
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      ),
    },

    {
      label: "Contact Us",
      id: "contact",
      path: "/contact-us",
      icon: (
        <svg
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
          <path d="M8 9h8" />
          <path d="M8 13h5" />
        </svg>
      ),
    },

    {
      label: "Help & Support",
      id: "help",
      path: "/help-support",
      icon: (
        <svg
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.5 9a2.7 2.7 0 1 1 4.6 1.9c-.9.9-2.1 1.3-2.1 2.6" />
          <circle cx="12" cy="17" r="1" />
        </svg>
      ),
    },
  ];

  /* =========================================================
     HANDLE MENU NAVIGATION
  ========================================================= */

  const handleNavigation = (item) => {
    setActiveItem(item.label);

    if (onClose) {
      onClose();
    }

    if (basePath) {
      navigate(`${basePath}${item.path}`);
    } else {
      navigate(item.path);
    }
  };

  /* =========================================================
     HANDLE LOGOUT
  ========================================================= */

  const handleLogout = () => {
    if (onClose) {
      onClose();
    }

    if (userId && deviceUuid) {
      navigate(
        `/${userId}/${deviceUuid}/userandpartner`,
        { replace: true }
      );
    } else {
      navigate("/userandpartner", { replace: true });
    }
  };

  /* =========================================================
     HANDLE RATE US
  ========================================================= */

  const handleRateUs = () => {
    console.log("Rate Us clicked");
  };

  /* =========================================================
     HANDLE SHARE
  ========================================================= */

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "BroBizee",
          text: "Check out BroBizee",
        });
      } else {
        console.log("Web Share API is not supported.");
      }
    } catch (error) {
      console.log("Share cancelled:", error);
    }
  };

  /* =========================================================
     PROFILE IMAGE  — with error fallback
  ========================================================= */

  const profileImage =
    !imageError && user?.profile_url
      ? user.profile_url
      : null;

  /* =========================================================
     USER NAME
  ========================================================= */

  const userName = user?.name || "User";

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div
      className={`
        ${styles.sliderOverlay}
        ${isOpen ? styles.open : ""}
      `}
      aria-hidden={!isOpen}
    >
      {/* =====================================================
          BACKDROP
      ====================================================== */}

      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* =====================================================
          DRAWER
      ====================================================== */}

      <aside
        className={styles.drawerContainer}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation Menu"
      >
        {/* ===================================================
            SCROLL CONTENT
        ==================================================== */}

        <div className={styles.scrollContent}>

          {/* =================================================
              TOP LOGO
          ================================================= */}

          <div className={styles.logoWrapper}>
            <div className={styles.brandLogo}>
              <span className={styles.brandText}>
                Bro
                <span className={styles.brandTextAccent}>
                  Bizee
                </span>
              </span>
            </div>

            {/* CLOSE BUTTON */}
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close Menu"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </div>

          {/* =================================================
              PROFILE SECTION
          ================================================= */}

          <div className={styles.profileSection}>

            {/* AVATAR */}
            <div className={styles.avatarRing}>
              {profileImage ? (
                <img
                  src={profileImage}
                  alt={userName}
                  className={styles.avatarImage}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className={styles.avatarFallback}>
                  {userName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* USER NAME */}
            <h2 className={styles.userName}>
              {loadingUser ? "Loading..." : userName}
            </h2>

            {/* USER MOBILE */}
            {user?.mobile_number && (
              <p className={styles.userMobile}>
                {user.mobile_number}
              </p>
            )}

            {/* ERROR */}
            {!loadingUser && userError && (
              <small
                style={{
                  color: "#999",
                  marginTop: "5px",
                  display: "block",
                  textAlign: "center",
                }}
              >
                {userError}
              </small>
            )}
          </div>

          {/* =================================================
              NAVIGATION MENU
          ================================================= */}

          <nav
            className={styles.navMenu}
            aria-label="Side Navigation"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`
                  ${styles.navItem}
                  ${
                    activeItem === item.label
                      ? styles.active
                      : ""
                  }
                `}
                onClick={() => handleNavigation(item)}
                aria-current={
                  activeItem === item.label
                    ? "page"
                    : undefined
                }
              >
                {/* ICON */}
                <span className={styles.navIcon}>
                  {item.icon}
                </span>

                {/* LABEL */}
                <span className={styles.navLabel}>
                  {item.label}
                </span>

                {/* ARROW */}
                <span className={styles.navArrow}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </span>
              </button>
            ))}
          </nav>

          {/* =================================================
              VERSION
          ================================================= */}

          <div className={styles.versionText}>
            <span>BroBizee</span>
            <span>Version 0.0.1</span>
          </div>

          {/* =================================================
              LOGOUT
          ================================================= */}

          <div className={styles.actionSection}>
            <button
              type="button"
              className={styles.logoutButton}
              onClick={handleLogout}
              aria-label="Logout"
            >
              <span className={styles.logoutIcon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </span>

              <span>Logout</span>
            </button>
          </div>

          {/* =================================================
              FOOTER ACTIONS
          ================================================= */}

          <div className={styles.footerActions}>

            {/* RATE US */}
            <button
              type="button"
              className={styles.footerBtn}
              onClick={handleRateUs}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polygon points="12 2 15 9 22 9 17 14 19 21 12 17 5 21 7 14 2 9 9 9 12 2" />
              </svg>

              <span>Rate Us</span>
            </button>

            {/* SHARE */}
            <button
              type="button"
              className={styles.footerBtn}
              onClick={handleShare}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line
                  x1="8.59"
                  y1="13.51"
                  x2="15.42"
                  y2="17.49"
                />
                <line
                  x1="15.41"
                  y1="6.51"
                  x2="8.59"
                  y2="10.49"
                />
              </svg>

              <span>Share</span>
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}